// src/components/admin/bookings/BookingManagement.jsx
import React, { useState, useEffect, useContext } from "react";
import { Button, Dropdown, Table, Modal } from "react-bootstrap";
import {
  FiSearch, FiEye, FiCalendar, FiCheckCircle, FiClock,
  FiX, FiMapPin, FiUser, FiHash, FiTruck, FiDollarSign,
  FiFileText, FiFilter,
} from "react-icons/fi";
import AppPagination from "../../../components/general/pagination/Pagination.js";
import "../../../assets/css/superadmin/bookingmanagement/Bookingmanagement.css";
import MasterLayout from "../../../layout/superadmin/MasterLayout";
import axios from "axios";
import AppContext from "../../../context/AppContext.js";

const AVATAR_COLORS = [
  "#FDB714", "#FFD166", "#F4A261", "#E76F51",
  "#6BCB77", "#4D96FF", "#C77DFF", "#FF6B9D",
];

const TABS = [
  { label: "Pending", icon: <FiClock size={14} />, status: "pending" },
  { label: "Confirmed", icon: <FiCalendar size={14} />, status: "confirmed" },
  { label: "Completed", icon: <FiCheckCircle size={14} />, status: "completed" },
];

const getStatusClass = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "confirmed" || s === "inprogress" || s === "in-progress") return "bm-badge-confirmed";
  if (s === "pending") return "bm-badge-pending";
  if (s === "completed") return "bm-badge-completed";
  return "bm-badge-pending";
};

const getStatusLabel = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "inprogress" || s === "in-progress") return "In Progress";
  return (status || "").charAt(0).toUpperCase() + (status || "").slice(1);
};

const getPaymentClass = (payment) => {
  const p = (payment || "").toLowerCase();
  return p === "paid" ? "bm-pay-paid" : "bm-pay-pending";
};

// ─── SKELETONS ───────────────────────────────────────────────
const StatCardsSkeleton = () => (
  <>
    {[1, 2, 3, 4].map((i) => (
      <div className="col-6 col-lg-3" key={i}>
        <div className="bm-stat-card-skeleton">
          <span className="bm-skeleton bm-skeleton-icon" />
          <div className="bm-stat-content">
            <span className="bm-skeleton bm-skeleton-value" />
            <span className="bm-skeleton bm-skeleton-label" style={{ marginTop: 6 }} />
          </div>
        </div>
      </div>
    ))}
  </>
);

const TabsSkeleton = () => (
  <div className="bm-outer-tabs-wrap">
    <div className="bm-outer-tabs" style={{ paddingBottom: 10 }}>
      {[1, 2, 3].map((i) => (
        <span key={i} className="bm-skeleton bm-skeleton-tab" style={{ margin: "0 16px" }} />
      ))}
    </div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="bm-skeleton-row">
    <td>
      <div className="bm-cust-cell">
        <span className="bm-skeleton bm-skeleton-avatar" />
        <span className="bm-skeleton bm-skeleton-name" />
      </div>
    </td>
    <td>
      <div className="bm-cust-cell">
        <span className="bm-skeleton bm-skeleton-avatar" />
        <div>
          <span className="bm-skeleton bm-skeleton-name" style={{ display: "block" }} />
          <span className="bm-skeleton bm-skeleton-email" style={{ display: "block" }} />
        </div>
      </div>
    </td>
    <td><span className="bm-skeleton bm-skeleton-text-sm" /></td>
    <td><span className="bm-skeleton bm-skeleton-text-md" /></td>
    <td><span className="bm-skeleton bm-skeleton-status" /></td>
    <td><span className="bm-skeleton bm-skeleton-action" /></td>
  </tr>
);

// ═══════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════
const BookingManagement = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [bookingsData, setBookingsData] = useState([]);
  const { baseUrl, baseUrlForFile ,authToken} = useContext(AppContext);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1800);
    return () => clearTimeout(timer);
  }, []);

  // ── Stats ──────────────────────────────────────────────────
  const totalCount = bookingsData.length;
  const pendingCount = bookingsData.filter(b => (b.status || "").toLowerCase() === "pending").length;
  const confirmedCount = bookingsData.filter(b =>
    ["confirmed", "inprogress", "in-progress"].includes((b.status || "").toLowerCase())
  ).length;
  const completedCount = bookingsData.filter(b => (b.status || "").toLowerCase() === "completed").length;

  const STATS = [
    { label: "Total Bookings", value: totalCount, icon: <FiCalendar size={30} /> },
    { label: "Pending Bookings", value: pendingCount, icon: <FiClock size={30} /> },
    { label: "Confirmed Bookings", value: confirmedCount, icon: <FiFilter size={30} /> },
    { label: "Completed Bookings", value: completedCount, icon: <FiCheckCircle size={30} /> },
  ];

  // ── Filter ─────────────────────────────────────────────────
  const filtered = bookingsData.filter((b) => {
    const s = (b.status || "").toLowerCase();
    let matchTab = false;
    if (activeTab === "pending") matchTab = s === "pending";
    else if (activeTab === "confirmed") matchTab = s === "confirmed" || s === "inprogress" || s === "in-progress";
    else if (activeTab === "completed") matchTab = s === "completed";

    const customerName = b.srcustomer?.fullname || "";
    const equipmentName = b.srequipmentmanagment?.equipmentname || "";
    const q = searchQuery.toLowerCase();
    const matchSearch = !q ||
      String(b.id).includes(q) ||
      customerName.toLowerCase().includes(q) ||
      equipmentName.toLowerCase().includes(q);

    return matchTab && matchSearch;
  });

  const itemsPerPage = 5;
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedData = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleViewDetail = (booking) => { setSelectedBooking(booking); setShowModal(true); };
  const handleCloseModal = () => { setShowModal(false); setSelectedBooking(null); };

  const getAvatarColor = (id) => AVATAR_COLORS[(id || 0) % AVATAR_COLORS.length];
  const getInitial = (name) => (name || "?").charAt(0).toUpperCase();

  // ── Fetch ──────────────────────────────────────────────────
  const fetchAllRentalBookings = () => {
    axios
      .get(`${baseUrl}/superadmin/rentalBookings/fetchAllRentalBookings`,{
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then((res) => {
        if (res.data.success === true) {
          setBookingsData(res.data.rentalrequests);
        }
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    fetchAllRentalBookings();
  }, []);

  // ── Helpers ────────────────────────────────────────────────
  const getCustomerName = (b) => b.srcustomer?.fullname || "—";
  const getEquipmentName = (b) => b.srequipmentmanagment?.equipmentname || "—";
  const getRentalAdminName = (b) => b.srrentaladmin?.fullname || "—";
  const getShopName = (b) => b.srrentaladmin?.srbusinessdetails?.businessname || "—";
  const getPaymentStatus = (b) => b.payment || "—";

  // ════════════════════════════════════════════════════════════
  return (
    <MasterLayout>

      {/* ── Page Header ── */}
      <div className="d-flex align-items-start justify-content-between bm-header">
        <div>
          <h1>Booking Management</h1>
          <p>Monitor and manage all machinery bookings and reservations.</p>
        </div>
      </div>

      {/* ── Stat Cards ── */}
      <div className="row g-3 mb-4">
        {isLoading ? <StatCardsSkeleton /> : STATS.map((s, i) => (
          <div className="col-6 col-lg-3" key={i}>
            <div className="bm-stat-card">
              <div className="bm-stat-icon">{s.icon}</div>
              <div className="bm-stat-content">
                <h3 className="bm-stat-value">{s.value}</h3>
                <p className="bm-stat-label">{s.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      {isLoading ? <TabsSkeleton /> : (
        <div className="bm-outer-tabs-wrap">
          <div className="bm-outer-tabs">
            {TABS.map((tab) => {
              const count = tab.status === "confirmed"
                ? bookingsData.filter(b =>
                  ["confirmed", "inprogress", "in-progress"].includes((b.status || "").toLowerCase())
                ).length
                : bookingsData.filter(b =>
                  (b.status || "").toLowerCase() === tab.status
                ).length;
              return (
                <button
                  key={tab.status}
                  className={`bm-outer-tab ${activeTab === tab.status ? "bm-outer-tab-active" : ""}`}
                  onClick={() => { setActiveTab(tab.status); setCurrentPage(1); }}
                >
                  {tab.icon} {tab.label}
                  <span className="bm-outer-tab-count">({count})</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Table Card ── */}
      <div className="bm-table-card">
        <div className="bm-table-heading">
          <h2 className="bm-table-title">Recent Bookings</h2>
        </div>

        <Table className="bm-table" responsive={false}>
          <thead>
            <tr>
              <th>CUSTOMER</th>
              <th>RENTALADMIN</th>

              <th>EQUIPMENT</th>
              <th>TOTAL PRICE</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, idx) => <SkeletonTableRow key={idx} />)
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={6} className="bm-empty-row">No bookings found for this filter.</td>
              </tr>
            ) : (
              paginatedData.map((b) => (
                <tr>


                  {/* ✅ FIX: Customer — ab td ke andar */}
                  <td>
                    <div className="bm-cust-cell">
                      <img
                        src={`${baseUrlForFile}/${b.srcustomer?.image}`}
                        alt=""
                        className="bm-avatar-img"
                      />
                      <div>
                        <div className="bm-customer-name">
                          {b.srcustomer?.fullname || "—"}
                        </div>
                        <div className="bm-customer-email">
                          {b.srcustomer?.email}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* ✅ FIX: Rental Admin — ab td ke andar */}
                  <td>
                    <div className="bm-cust-cell">
                      <img
                        src={`${baseUrlForFile}/${b.srrentaladmin?.image}`}
                        alt=""
                        className="bm-avatar-img"
                      />
                      <div>
                        <div className="bm-customer-name">
                          {b.srrentaladmin?.fullname || "—"}
                        </div>
                        <div className="bm-customer-email">
                          {b.srrentaladmin?.email}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Equipment */}
                  <td className="bm-items-col">{getEquipmentName(b)}</td>

                  {/* Total Price */}
                  <td>
                    <span className="bm-total-col">
                      Rs. {Number(b.totalprice || 0).toLocaleString()}
                    </span>
                  </td>

                  {/* Status */}
                  <td>
                    <span className={`bm-badge ${getStatusClass(b.status)}`}>
                      {getStatusLabel(b.status)}
                    </span>
                  </td>

                  {/* Actions */}
                  <td>
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        as="button"
                        className="bm-dots-btn"
                        id={`drop-${b.id}`}
                        bsPrefix="bm-dots-btn"
                      >
                        ⋮
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="bm-dropdown-menu">
                        <Dropdown.Item onClick={() => handleViewDetail(b)}>
                          <FiEye className="item-icon" /> View Detail
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {!isLoading && (
          <AppPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filtered.length}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        )}
      </div>

      {/* ── Detail Modal ── */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg" className="bm-detail-modal">
        {selectedBooking && (
          <>
            <div className="bm-modal-header">
              <div className="bm-modal-header-left">
                <h5>Booking Detail</h5>
                <span>Full summary and equipment information</span>
              </div>
              <Button className="bm-modal-close" onClick={handleCloseModal}><FiX /></Button>
            </div>

            <Modal.Body className="bm-modal-body">

              {/* Status Bar */}
              <div className="bm-modal-status-bar">
                <div className="status-left">
                  <FiHash size={16} color="#FDB714" />
                  <span className={`bm-badge ${getStatusClass(selectedBooking.status)}`}>
                    {getStatusLabel(selectedBooking.status)}
                  </span>
                </div>
                <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                  <FiCalendar size={13} style={{ marginRight: 5 }} />
                  {selectedBooking.bookingdate || "—"}
                </div>
              </div>

              {/* Customer Info */}
              <p className="bm-section-title"><FiUser size={15} /> Customer Information</p>
              <div className="bm-customer-strip">
                <img
                  src={`${baseUrlForFile}/${selectedBooking.srrentaladmin?.image}`}
                  alt=""
                  className="bm-avatar-img"
                />
                <div style={{ flex: 1 }}>
                  <div className="cust-name">{getCustomerName(selectedBooking)}</div>
                  <div className="cust-meta">
                     {selectedBooking.srcustomer?.email}
                  </div>
                </div>
              </div>

              {/* Booking Summary */}
              <p className="bm-section-title"><FiFileText size={15} /> Booking Summary</p>
              <div className="bm-info-grid">
                <div className="bm-info-item">
                  <label>Rental Admin Name</label>
                  <span>{getRentalAdminName(selectedBooking)}</span>
                </div>
                <div className="bm-info-item">
                  <label>Shop Name</label>
                  <span>{getShopName(selectedBooking)}</span>
                </div>
                <div className="bm-info-item">
                  <label>Equipment Name</label>
                  <span>{getEquipmentName(selectedBooking)}</span>
                </div>
                <div className="bm-info-item">
                  <label>Location</label>
                  <span>{selectedBooking.location || "—"}</span>
                </div>
                <div className="bm-info-item">
                  <label>Booking Date</label>
                  <span>{selectedBooking.bookingdate || "—"}</span>
                </div>
                <div className="bm-info-item">
                  <label>Duration</label>
                  <span>{selectedBooking.duration || "—"} day(s)</span>
                </div>
              </div>

              {/* Payment Summary */}
              <p className="bm-section-title"><FiDollarSign size={15} /> Payment Summary</p>
              <div className="bm-summary-box">
                <div className="bm-summary-row">
                  <span>
                    {getEquipmentName(selectedBooking)} ({selectedBooking.duration || "—"} days)
                  </span>
                  <span>Rs. {Number(selectedBooking.totalprice || 0).toLocaleString()}</span>
                </div>
                <div className="bm-summary-row total">
                  <span>Total Amount</span>
                  <span>Rs. {Number(selectedBooking.totalprice || 0).toLocaleString()}</span>
                </div>
                {/* ✅ NEW: payment badge — Total Amount ke neeche, right side */}
                <div className="bm-summary-payment-row">
                  <span className={`bm-pay-badge ${getPaymentClass(selectedBooking.payment)}`}>
                    {getPaymentStatus(selectedBooking).toUpperCase()}
                  </span>
                </div>
              </div>

            </Modal.Body>

            <Modal.Footer className="bm-modal-footer">
              <Button className="bm-close-detail-btn" onClick={handleCloseModal}>Close</Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

    </MasterLayout>
  );
};

export default BookingManagement;